import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import BRAND from "../brand";
import { ArrowLeft, LogOut } from "lucide-react";
import { ProfileInfoCard, ReadinessScoreCard, QuickStatsCard } from "./profile/ProfileComponents";
import { RecentSessionsCard } from "./profile/RecentSessionsCard";

export default function ProfilePage() {
  const { userId } = useParams();
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const targetUserId = userId || currentUser?.id;

  useEffect(() => {
    if (!targetUserId) return;
    const fetchProfile = async () => {
      try {
        const { data } = await axios.get(`${API}/profile/${targetUserId}`);
        setProfile(data);
      } catch {
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [targetUserId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-[#2563EB] rounded-full animate-spin"></div>
          <p className="text-slate-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const isOwnProfile = currentUser?.id === profile.id;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to={currentUser?.role === "admin" ? "/admin" : "/dashboard"}>
                <Button variant="ghost" size="icon" className="text-slate-500 hover:text-[#0B1F3B]">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-7 w-auto" onError={(e) => { e.target.style.display = "none"; }} />
              <div>
                <h1 className="text-lg font-bold text-[#0B1F3B]">{isOwnProfile ? "My Profile" : "User Profile"}</h1>
                <p className="text-xs text-slate-400">{BRAND.productName}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <nav className="hidden md:flex items-center gap-6">
                <Link to="/dashboard" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Dashboard</Link>
                <Link to="/simulator" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Practice</Link>
              </nav>
              <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} className="text-slate-500 hover:text-[#0B1F3B]">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 fade-up">
          <Card className="premium-card lg:col-span-1" data-testid="profile-info-card">
            <CardContent className="p-0">
              <ProfileInfoCard profile={profile} />
            </CardContent>
          </Card>
          <Card className="premium-card" data-testid="profile-readiness-card">
            <CardContent className="p-0">
              <ReadinessScoreCard profile={profile} />
            </CardContent>
          </Card>
          <Card className="premium-card" data-testid="profile-stats-card">
            <CardContent className="p-0">
              <QuickStatsCard profile={profile} />
            </CardContent>
          </Card>
        </div>

        <RecentSessionsCard sessions={profile.recent_sessions} isOwnProfile={isOwnProfile} />
      </main>

      <footer className="border-t border-slate-200/60 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-sm text-slate-400">{BRAND.productName}</span>
            <div className="text-center sm:text-right">
              <p className="text-xs text-slate-400">{BRAND.poweredBy}</p>
              <p className="text-xs text-slate-300">{BRAND.companyAttribution}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
